import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { useResizeObserver } from "@memento/ui/hooks/useResizeObserver";
import { LoadingStatus } from "src/libs/fetcher";
import { useCardThemed } from "./useCardThemed";
import { useViewport } from "src/hooks/useViewport";
import { type CardThemeIdentifier } from "./card-theme-utils";

export type ThemeContentCutoutProps = {
  themeId?: CardThemeIdentifier;
  cutoutRadius: number;
  cutoutOffset?: number;
  children: ReactNode;
  hideCutout?: boolean;
};

/* Circular cut-out from:
 * https://ishadeed.com/article/thinking-about-the-cut-out-effect/
 *
 * NOTE: Because of the way the SVG mask works, only the
 * `theme.contentBackground.color` will be used for the background (i.e. it
 * won't bet able to use a gradient or an image).
 * */
export function ThemeContentCutout({
  themeId,
  cutoutRadius,
  cutoutOffset = 0,
  hideCutout = false,
  children,
}: ThemeContentCutoutProps) {
  // a unique id of the cutout mask
  const [maskId] = useState("hole-" + nanoid());
  const { theme, status } = useCardThemed(themeId);
  const { width: viewportWidth } = useViewport();
  const containerRef = useRef<HTMLDivElement>(null);

  const updateNotchMaskDimensions = useCallback(() => {
    if (containerRef.current) {
      setNotchMaskDimensions({
        width: containerRef.current.scrollWidth,
        height: containerRef.current.scrollHeight,
      });
    }
  }, [containerRef]);

  useResizeObserver(containerRef.current, updateNotchMaskDimensions);

  // There's a bug in Chrome that doesn't reliably propagate the width of a
  // parent element to an svg mask element, which is what we use for the
  // "notch", so we need to manually set it to the size of the parent.
  const [notchMaskDimensions, setNotchMaskDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(updateNotchMaskDimensions, [
    children,
    cutoutRadius,
    viewportWidth,
    updateNotchMaskDimensions,
  ]);

  if (status !== LoadingStatus.SUCCESS) {
    return null;
  }

  return (
    <div className="relative h-full w-full" ref={containerRef}>
      <svg role="none" className="absolute top-0 left-0 h-full w-full">
        {hideCutout ? null : (
          <defs>
            <mask
              id={maskId}
              width={notchMaskDimensions?.width}
              height={notchMaskDimensions?.height}
            >
              <rect
                width={notchMaskDimensions?.width || "100%"}
                height={notchMaskDimensions?.height || "100%"}
                fill="white"
              />
              <circle
                cx="50%"
                cy={`${cutoutOffset}px`}
                r={cutoutRadius}
                fill="black"
              ></circle>
            </mask>
          </defs>
        )}

        <rect
          width="100%"
          height="100%"
          mask={hideCutout ? undefined : `url(#${maskId})`}
          style={{
            fill: theme.contentBackgroundColor || "transparent",
          }}
        />
      </svg>
      <div className="relative z-10 h-full w-full">{children}</div>
    </div>
  );
}
